var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

// external dependencies
import cookies from 'browser-cookies';

// constants
import { DEFAULT_OPTIONS, FUNCTION_NAME_REGEXP, getNow } from './constants';

/**
 * @function getExistingCookieValues
 *
 * @description
 * get the cookie values available, defaulting to an empty object
 *
 * @param {string} key the cookie key
 * @returns {Object} the stored values
 */
export var getExistingCookieValues = function getExistingCookieValues(key) {
  return JSON.parse(cookies.get(key) || '{}');
};

/**
 * @function getCalculatedNewState
 *
 * @description
 * get the new state calculated based on the options passed
 *
 * @param {Object} options the options passed to the decorator
 * @param {string} options.key the key to get the existing values from in cookies
 * @param {Object} state the current state of the component instance
 * @param {number} state.idleTimestamp the current idleTimestamp in state
 * @param {number} state.timeoutTimestamp the current timeoutTimestamp in state
 * @returns {Object} the calculated new state of the component instance
 */
export var getCalculatedNewState = function getCalculatedNewState(_ref, _ref2) {
  var key = _ref.key;
  var currentIdleTimestamp = _ref2.idleTimestamp,
      currentTimeoutTimestamp = _ref2.timeoutTimestamp;

  var _getExistingCookieVal = getExistingCookieValues(key),
      _getExistingCookieVal2 = _getExistingCookieVal.idleTimestamp,
      idleTimestamp = _getExistingCookieVal2 === undefined ? currentIdleTimestamp : _getExistingCookieVal2,
      _getExistingCookieVal3 = _getExistingCookieVal.timeoutTimestamp,
      timeoutTimestamp = _getExistingCookieVal3 === undefined ? currentTimeoutTimestamp : _getExistingCookieVal3;

  var now = getNow();

  return {
    idleIn: Math.max(idleTimestamp - now, 0),
    idleTimestamp: idleTimestamp,
    isIdle: now >= idleTimestamp,
    isTimedOut: now >= timeoutTimestamp,
    timeoutIn: Math.max(timeoutTimestamp - now, 0),
    timeoutTimestamp: timeoutTimestamp
  };
};

/**
 * @function getCalculatedTimestamps
 *
 * @description
 * get the calculated timestamp-related data for the component state
 *
 * @param {Object} options the options passed to the decorator
 * @param {number} [options.idleAfter=DEFAULT_OPTIONS.idleAfter] the number of ms to wait before being idle
 * @param {number} [options.timeoutAfter=DEFAULT_OPTIONS.timeoutAfter] the number of ms to wait before being timedout
 * @returns {Object} the calculated timestamps object
 */
export var getCalculatedTimestamps = function getCalculatedTimestamps(_ref3) {
  var _ref3$idleAfter = _ref3.idleAfter,
      idleAfter = _ref3$idleAfter === undefined ? DEFAULT_OPTIONS.idleAfter : _ref3$idleAfter,
      _ref3$timeoutAfter = _ref3.timeoutAfter,
      timeoutAfter = _ref3$timeoutAfter === undefined ? DEFAULT_OPTIONS.timeoutAfter : _ref3$timeoutAfter;

  var now = getNow();

  return {
    idleAfter: idleAfter,
    idleTimestamp: now + idleAfter,
    timeoutAfter: timeoutAfter,
    timeoutTimestamp: now + idleAfter + timeoutAfter
  };
};

/**
 * @private
 *
 * @function getFunctionNameViaRegexp
 *
 * @description
 * use regexp match on stringified function to get the function name
 *
 * @param {function} fn function to get the name of
 * @returns {string} function name
 */
export var getFunctionNameViaRegexp = function getFunctionNameViaRegexp(fn) {
  return (fn.toString().match(FUNCTION_NAME_REGEXP) || [])[1] || '';
};

/**
 * @private
 *
 * @function getComponentName
 *
 * @description
 * get the component name, either from modern property or regexp match, falling back to generic string
 *
 * @param {function} fn function to get the name of
 * @returns {string} function name
 */
export var getComponentName = function getComponentName(fn) {
  return fn.displayName || fn.name || getFunctionNameViaRegexp(fn) || 'Component';
};

/**
 * @function getNormalizedOptions
 *
 * @description
 * get the options normalized with the defaults
 *
 * @param {Object} options the options passed to the decorator
 * @returns {Object} the coalesced options
 */
export var getNormalizedOptions = function getNormalizedOptions(options) {
  return _extends({}, DEFAULT_OPTIONS, options);
};

/**
 * @function setCookieValues
 *
 * @description
 * set the cookie values based on the options and state passed
 *
 * @param {Object} options the options passed to the decorator
 * @param {string} options.key the key to get the existing values from in cookies
 * @param {Object} [options.storageOptions] the options to apply when setting the cookies
 * @param {Object} values the object of values to assign
 * @param {number} values.idleTimestamp the timestamp of when the next idle state will occur
 * @param {number} values.timeoutTimestamp the timestamp of when the next timeout state will occur
 * @returns {void}
 */
export var setCookieValues = function setCookieValues(_ref4, _ref5) {
  var key = _ref4.key,
      storageOptions = _ref4.storageOptions;
  var idleTimestamp = _ref5.idleTimestamp,
      timeoutTimestamp = _ref5.timeoutTimestamp;
  return cookies.set(key, JSON.stringify({
    idleTimestamp: idleTimestamp,
    timeoutTimestamp: timeoutTimestamp
  }), storageOptions);
};

/**
 * @function getFreshState
 *
 * @description
 * get a refreshed state object based on the options passed
 *
 * @param {Object} options the options passed to the decorator
 * @returns {Object} the refreshed state values
 */
export var getFreshState = function getFreshState(options) {
  var _getCalculatedTimesta = getCalculatedTimestamps(options),
      idleAfter = _getCalculatedTimesta.idleAfter,
      idleTimestamp = _getCalculatedTimesta.idleTimestamp,
      timeoutAfter = _getCalculatedTimesta.timeoutAfter,
      timeoutTimestamp = _getCalculatedTimesta.timeoutTimestamp;

  setCookieValues(options, {
    idleTimestamp: idleTimestamp,
    timeoutTimestamp: timeoutTimestamp
  });

  return {
    idleIn: idleAfter,
    idleTimestamp: idleTimestamp,
    isIdle: false,
    isTimedOut: false,
    timeoutIn: idleAfter + timeoutAfter,
    timeoutTimestamp: timeoutTimestamp
  };
};

/**
 * @function shouldSetState
 *
 * @description
 * should the new state passed be applied to the component and passed to the child
 *
 * @param {Object} newState the new state to potentially apply
 * @param {Object} currentState the current state of the component instance
 * @returns {boolean} should the new state be applied to the component isntance
 */
export var shouldSetState = function shouldSetState(newState, currentState) {
  return newState.isIdle ? !currentState.isIdle || newState.isTimedOut !== currentState.isTimedOut || newState.timeoutIn !== currentState.timeoutIn : currentState.isIdle;
};